import type { Action } from 'react-sweet-state';
import { createStateHook, createActionsHook, createStore } from 'react-sweet-state';

import type { BillingSourceSystem } from '../queries/__types__/TrialDetailsQuery';

export type Entitlement = {
	transactionAccount?: {
		experienceCapabilities?: {
			addPaymentMethod?: {
				isAvailableToUser?: boolean;
			};
		};
	};
	experienceCapabilities?: {
		changeOffering?: {
			isAvailableToUser?: boolean;
		};
	};
};

type UserInfoState = {
	billingSourceSystem?: BillingSourceSystem | null;
	daysLeftInTrial?: number | null;
	entitlement?: Entitlement;
	hasBillingInformation?: boolean;
	hasModalBeenSeen?: boolean;
	isSiteAdmin?: boolean;
	isUserEligible?: boolean;
	modalKey?: string;
	trialEndDate?: string | null;
	userCanPayAndUpgrade?: boolean;
};

const initialState: UserInfoState = {
	billingSourceSystem: undefined,
	daysLeftInTrial: undefined,
	modalKey: undefined,
};

const actions = {
	setBillingSourceSystem:
		(billingSourceSystem?: BillingSourceSystem | null): Action<UserInfoState> =>
		({ setState }) => {
			setState({ billingSourceSystem });
		},
	setDaysLeftInTrial:
		(daysLeftInTrial?: number | null): Action<UserInfoState> =>
		({ setState }) => {
			setState({ daysLeftInTrial });
		},
	setEntitlement:
		(entitlement?: Entitlement): Action<UserInfoState> =>
		({ setState }) => {
			setState({ entitlement });
		},
	setHasBillingInformation:
		(hasBillingInformation?: boolean): Action<UserInfoState> =>
		({ setState }) => {
			setState({ hasBillingInformation: hasBillingInformation || false });
		},
	setHasModalBeenSeen:
		(hasModalBeenSeen: boolean): Action<UserInfoState> =>
		({ setState }) => {
			setState({ hasModalBeenSeen });
		},
	setIsSiteAdmin:
		(isSiteAdmin: boolean): Action<UserInfoState> =>
		({ setState }) => {
			setState({ isSiteAdmin });
		},
	setIsUserEligible:
		(isUserEligible: boolean): Action<UserInfoState> =>
		({ setState }) => {
			setState({ isUserEligible });
		},
	setModalKey:
		(modalKey: string): Action<UserInfoState> =>
		({ setState }) => {
			setState({ modalKey });
		},
	setTrialEndDate:
		(trialEndDate?: string | null): Action<UserInfoState> =>
		({ setState }) => {
			setState({ trialEndDate });
		},
	setUserCanPayAndUpgrade:
		(userCanPayAndUpgrade: boolean): Action<UserInfoState> =>
		({ setState }) => {
			setState({ userCanPayAndUpgrade });
		},
};

const Store = createStore<UserInfoState, typeof actions>({
	initialState,
	actions,
});

export const useUserInfoActions = createActionsHook(Store);
export const useUserInfo = createStateHook(Store);
